import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { ArrowRight } from "@phosphor-icons/react";
import { ANUploader } from "@answersnow/pkg-theme";

import { AFLS_STEP_CONFIGS, buildInitialAflsSteps } from "../../dataEntryConfig";
import { space3, space5 } from "../../styled";
import { aflsStepTotal } from "../../utils";
import type { AflsFormData, AflsStepState, AssessmentRecord } from "../../types";
import { AssessmentDatePicker, ScoreInputRow } from "./shared";
import { getUser } from "selectors";

interface AflsDataEntryProps {
  administratorName: string;
  previousAssessment: AssessmentRecord | null;
  onCancel: () => void;
  onSave: (data: AflsFormData) => void;
  /** True while a save is in flight; disables the finish button to prevent duplicate submits. */
  saving?: boolean;
}

/**
 * Build initial steps. If previous assessment data exists, mark all steps
 * as completed (not "Not Tested"). Sub-skill scores start empty since the
 * backend only stores domain totals.
 */
function buildInitialSteps(previous: AssessmentRecord | null): AflsStepState[] {
  const steps = buildInitialAflsSteps();
  if (!previous) return steps;

  // Previous data present — mark all steps as completed; sub-scores start empty.
  return steps.map((step) => ({ ...step, stepCompleted: true }));
}

/** Auto-calculates the domain total from the step's editable sub-skill scores.
 * Returns null (empty) until at least one score is entered; then sums all entered scores (null treated as 0). */
function calcStepTotal(step: AflsStepState): number | null {
  return aflsStepTotal(step);
}

/**
 * Multi-step data entry form for AFLS (Assessment of Functional Living Skills).
 * Walks through 6 skill domains, each with a list of scored items, then a final
 * step (Step 7) for uploading the AFLS Scoring Grid document.
 * Domain totals are auto-calculated from sub-skill scores (read-only).
 */
export default function AflsDataEntry({
  previousAssessment,
  onCancel,
  onSave,
  saving = false,
}: AflsDataEntryProps) {
  const currentUserName = useSelector(getUser)?.name ?? "";
  const [administrationDate, setAdministrationDate] = useState<string | null>(null);
  const [localAdminName, setLocalAdminName] = useState<string>(currentUserName);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState<AflsStepState[]>(() => buildInitialSteps(previousAssessment));
  const [uploadedFiles, setUploadedFiles] = useState<{ file: File; errors: any[] }[]>([]);

  // Sync Redux user name to state whenever it changes (for auto-population)
  useEffect(() => {
    setLocalAdminName(currentUserName);
  }, [currentUserName]);

  const totalSteps = AFLS_STEP_CONFIGS.length + 1;
  const DOCUMENT_STEP_INDEX = AFLS_STEP_CONFIGS.length;
  const isDocumentStep = currentStepIndex === DOCUMENT_STEP_INDEX;
  const stepConfig = !isDocumentStep ? AFLS_STEP_CONFIGS[currentStepIndex] : null;
  const stepState = !isDocumentStep ? steps[currentStepIndex] : null;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const progressValue = ((currentStepIndex + 1) / totalSteps) * 100;

  const stepTotal = useMemo(
    () => calcStepTotal(stepState ?? { stepCompleted: false, skills: [], stepId: 0 }),
    [stepState]
  );

  function updateSkillScore(skillId: string, value: string) {
    const parsed = value === "" ? null : Number(value);
    setSteps((prev) =>
      prev.map((s, idx) =>
        idx === currentStepIndex
          ? {
              ...s,
              skills: s.skills.map((sk) => (sk.id === skillId ? { ...sk, score: parsed } : sk)),
            }
          : s
      )
    );
  }

  function toggleStepCompleted() {
    setSteps((prev) =>
      prev.map((s, idx) =>
        idx === currentStepIndex ? { ...s, stepCompleted: !s.stepCompleted } : s
      )
    );
  }

  function handleNext() {
    if (!isLastStep) {
      setCurrentStepIndex((i) => i + 1);
    }
  }

  function handleBack() {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((i) => i - 1);
    }
  }

  function handleSave() {
    onSave({ administrationDate, administratorName: localAdminName, steps, uploadedFiles });
  }

  return (
    <Stack component="form" sx={{ height: "100%" }} noValidate>
      {/* Scrollable content area */}
      <Box sx={{ overflowY: "auto", flex: 1, pr: 1 }}>
        <Stack rowGap={space5}>
          {/* Step progress bar */}
          <Stack rowGap={space3}>
            <Stack direction="row" alignItems="center" columnGap={space3}>
              <Typography variant="subtitle2" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                {isDocumentStep ? "Scoring Grid" : stepConfig!.title}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: "#EFE8FF",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                    backgroundColor: "#8E42CA",
                  },
                }}
              />
              <Typography color="text.secondary" variant="body2" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                Step {currentStepIndex + 1} of {totalSteps}
              </Typography>
            </Stack>
          </Stack>

          {isDocumentStep ? (
            /* Step 7: AFLS Scoring Grid document upload */
            <Stack rowGap={space3}>
              <Typography variant="body2" fontWeight={500}>
                AFLS Scoring Grid
              </Typography>
              <Box>
                <ANUploader
                  dropzoneFiles={uploadedFiles}
                  alreadyUploadedFiles={[]}
                  handleFileUpload={({
                    acceptedFiles,
                    fileRejections,
                  }: {
                    acceptedFiles: File[];
                    fileRejections: any[];
                  }) => {
                    const wrapped = [
                      ...acceptedFiles.map((f) => ({ file: f, errors: false })),
                      ...(fileRejections ?? []).map((f) => ({ file: f.file, errors: true })),
                    ];
                    setUploadedFiles((prev) => [...prev, ...wrapped]);
                  }}
                  handleFileDelete={(file: { file: File; errors: any }) =>
                    setUploadedFiles((prev) => prev.filter((f) => f !== file))
                  }
                  handleLoadedFileDelete={() => {}}
                  maxFileSize={3 * 1024 * 1024}
                  useDropzoneOptions={{
                    accept: {
                      "image/png": [],
                      "image/jpeg": [],
                      "application/pdf": [],
                    },
                    multiple: false,
                  }}
                />
              </Box>
            </Stack>
          ) : (
            <>
              {/* Step inclusion checkbox */}
              <Stack rowGap={space3}>
                <Box
                  sx={{
                    backgroundColor: "#F6F6F8",
                    border: "1px solid #D3D5DC",
                    borderRadius: 3,
                    p: 7
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={stepState!.stepCompleted}
                        onChange={toggleStepCompleted}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Stack rowGap={1}>
                        <Typography variant="body2">{stepConfig!.question}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Deselect to skip this section. It will be reported as &quot;Not Tested&quot;.
                        </Typography>
                      </Stack>
                    }
                    sx={{ alignItems: "flex-start", m: 0 }}
                  />
                </Box>
              </Stack>

              {/* Administration date — only shown on step 1 */}
              {currentStepIndex === 0 && (
                <Stack rowGap={space3}>
                  <TextField
                    label="Administrator Name"
                    size="small"
                    value={localAdminName}
                    onChange={(e) => setLocalAdminName(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <AssessmentDatePicker
                    value={administrationDate}
                    onChange={setAdministrationDate}
                  />
                </Stack>
              )}

              {/* Skill score rows + auto-calculated domain total */}
              <Stack rowGap={space3}>
                {/* Auto-calculated domain total (read-only) — shown first, formatted like other score rows */}
                <ScoreInputRow
                  label={stepConfig!.skills[0]?.name ?? "Total"}
                  value={stepTotal}
                  max={stepConfig!.skills[0]?.maxScore ?? 0}
                  onChange={() => {}}
                  readOnly
                  disabled={!stepState!.stepCompleted}
                />
                {stepState!.skills.map((skill) => (
                  <ScoreInputRow
                    key={skill.id}
                    label={skill.name}
                    value={skill.score}
                    max={skill.maxScore}
                    onChange={(value) => updateSkillScore(skill.id, value)}
                    disabled={!stepState!.stepCompleted}
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Box>

      {/* Step navigation footer — always visible */}
      <Box sx={{ pt: space3, flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant={currentStepIndex === 0 ? "contained" : "text"}
            color={currentStepIndex === 0 ? "secondary" : "primary"}
            onClick={currentStepIndex === 0 ? onCancel : handleBack}
          >
            {currentStepIndex === 0 ? "Cancel" : "Back"}
          </Button>

          {isLastStep ? (
            <Stack direction="row" columnGap={space3}>
              <Button variant="contained" color="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
              >
                Finish And Add Assessment
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              endIcon={
                currentStepIndex === AFLS_STEP_CONFIGS.length - 1 ? undefined : <ArrowRight weight="regular" />
              }
              sx={{ "& .MuiButton-endIcon svg": { width: 16, height: 16 } }}
            >
              {currentStepIndex === AFLS_STEP_CONFIGS.length - 1
                ? "Add AFLS Scoring Grid"
                : AFLS_STEP_CONFIGS[currentStepIndex + 1].title}
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
